/**
 * Codex-Nachprüfung PB-13 H-4: Statusabruf der Kasse (GET /kassen/:id/status) mit
 * Wiederholung. Ohne gespeicherten Modulstand startet die Kasse als Grundkasse; scheiterte der
 * einzige Abruf beim Start (Netz, Server kurz weg), blieb sie das bis zum Neuladen. Darum:
 * - nach einem Fehler erneut mit wachsender Pause (Backoff, Vorgabe 2 s bis 60 s), bis ein
 *   Abruf gelingt;
 * - bei Wiederverbindung (`online`) sofort erneut – auch nach einem Erfolg (gebuchte Module
 *   können sich inzwischen geändert haben);
 * - nach `beenden()` (Kassenwechsel, Abbau) wird kein Ergebnis mehr übernommen.
 */
export interface AbrufZeitgeber {
  setze(rueckruf: () => void, ms: number): unknown;
  loesche(handle: unknown): void;
}

const STANDARD_ZEITGEBER: AbrufZeitgeber = {
  setze: (rueckruf, ms) => setTimeout(rueckruf, ms),
  loesche: (handle) => {
    clearTimeout(handle as ReturnType<typeof setTimeout>);
  },
};

export class Wiederholabruf<T> {
  private timer: unknown = null;
  private laeuft = false;
  /** Während eines laufenden Abrufs angefordert (Wiederverbindung): danach erneut abrufen. */
  private erneut = false;
  private fehlversuche = 0;
  private beendet = false;
  private readonly erstePause: number;
  private readonly maxPause: number;
  private readonly zeitgeber: AbrufZeitgeber;

  constructor(
    private readonly laden: () => Promise<T>,
    private readonly anwenden: (wert: T) => void,
    opt: { erstePauseMs?: number; maxPauseMs?: number; zeitgeber?: AbrufZeitgeber } = {},
  ) {
    this.erstePause = opt.erstePauseMs ?? 2_000;
    this.maxPause = opt.maxPauseMs ?? 60_000;
    this.zeitgeber = opt.zeitgeber ?? STANDARD_ZEITGEBER;
  }

  /** Erster Abruf (beim Start bzw. Kassenwechsel). */
  starte(): void {
    void this.versuch();
  }

  /** Wiederverbindung: sofort erneut abrufen, Backoff zurücksetzen. */
  beiWiederverbindung(): void {
    if (this.beendet) {
      return;
    }
    this.fehlversuche = 0;
    if (this.laeuft) {
      this.erneut = true;
      return;
    }
    void this.versuch();
  }

  /** Abbau bzw. Kassenwechsel: keine Wiederholung, kein Ergebnis mehr übernehmen. */
  beenden(): void {
    this.beendet = true;
    this.timerLoeschen();
  }

  /** Pause vor dem nächsten Versuch nach `fehlversuche` Fehlern (für Tests sichtbar). */
  pauseNach(fehlversuche: number): number {
    return Math.min(this.maxPause, this.erstePause * 2 ** Math.max(0, fehlversuche - 1));
  }

  private timerLoeschen(): void {
    if (this.timer !== null) {
      this.zeitgeber.loesche(this.timer);
      this.timer = null;
    }
  }

  /** Abgebaut? (Methode statt Feldzugriff: der Zustand ändert sich während `await`.) */
  private istBeendet(): boolean {
    return this.beendet;
  }

  private async versuch(): Promise<void> {
    if (this.istBeendet() || this.laeuft) {
      return;
    }
    this.timerLoeschen();
    this.laeuft = true;
    let ok: boolean;
    try {
      const wert = await this.laden();
      if (!this.istBeendet()) {
        this.anwenden(wert);
      }
      ok = true;
    } catch {
      ok = false;
    } finally {
      this.laeuft = false;
    }
    if (this.istBeendet()) {
      return;
    }
    if (this.erneut) {
      this.erneut = false;
      void this.versuch();
      return;
    }
    if (ok) {
      this.fehlversuche = 0;
      return;
    }
    this.fehlversuche += 1;
    this.timer = this.zeitgeber.setze(() => {
      this.timer = null;
      void this.versuch();
    }, this.pauseNach(this.fehlversuche));
  }
}
